import * as React from "react";
import {
  HTMLTable,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  Dialog,
  FormGroup,
  InputGroup,
  TextArea,
  type OverlayToaster,
  Tag,
  Switch,
  NumericInput,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin, { IOrderType } from "../../../api/admin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useOutletContext } from "react-router-dom";
import { useAuthState } from "../../../context/auth";
import { isFybrAdminUser } from "../adminRoles";
import { useInternalCloudAdminGate } from "../useInternalCloudAdminGate";
import { roundedHoursFromSeconds } from "../../../utils/format";
import { DEFAULT_DEADLINE_HOURS } from "../../../constants";
import { stickyTableHeaderCss } from "../common/adminCss";
import { OrderTypeItemsEditor } from "../common/OrderTypeItemsEditor";

function OrderTypeEditDialog(props: {
  editOrderType: IOrderType | null;
  isCreate: boolean;
  onClose: () => void;
}) {
  const { editOrderType, isCreate, onClose } = props;

  const [editErrors, setEditErrors] = React.useState<string[]>([]);
  const [items, setItems] = React.useState<string[]>(
    () => editOrderType?.items ?? []
  );
  const [deadlineHours, setDeadlineHours] = React.useState(() => {
    if (isCreate || editOrderType?.deadline_duration_seconds == null) {
      return DEFAULT_DEADLINE_HOURS;
    }
    return roundedHoursFromSeconds(editOrderType.deadline_duration_seconds);
  });

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();
  const { currentUser } = useAuthState();

  const isFybrAdmin = isFybrAdminUser(currentUser);
  const organizationId = currentUser?.organization_id;

  const createOrderTypeMutation = useMutation({
    mutationFn: ({
      name,
      description,
      items,
      deadline_duration_seconds,
    }: {
      name: string;
      description?: string;
      items?: string[];
      deadline_duration_seconds: number;
    }) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can create order templates")
        );
      }
      return ApiAdmin.createOrderType({
        name,
        description,
        items,
        deadline_duration_seconds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderTypes"] });
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: ["orderTypes", organizationId],
        });
      }
      if (toaster.current) {
        toaster.current.show({
          message: "Order template created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to create order template. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const updateOrderTypeMutation = useMutation({
    mutationFn: ({
      orderTypeId,
      name,
      description,
      items,
      deadline_duration_seconds,
    }: {
      orderTypeId: number;
      name: string;
      description?: string;
      items?: string[];
      deadline_duration_seconds: number;
    }) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can update order templates")
        );
      }
      return ApiAdmin.updateOrderType(orderTypeId, {
        name,
        description,
        items,
        deadline_duration_seconds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderTypes"] });
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: ["orderTypes", organizationId],
        });
      }
      if (toaster.current) {
        toaster.current.show({
          message: "Order template updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to update order template. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "order-type-name"
    ) as HTMLInputElement;
    const descriptionInput = event.currentTarget.elements.namedItem(
      "order-type-description"
    ) as HTMLTextAreaElement;

    const name = nameInput?.value;
    const description = descriptionInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    if (!Number.isInteger(deadlineHours) || deadlineHours < 0) {
      errors.push("deadline");
    }

    if (errors.length === 0) {
      const deadline_duration_seconds = deadlineHours * 3600;
      if (isCreate) {
        createOrderTypeMutation.mutate({
          name: name.trim(),
          description: description?.trim() || undefined,
          items: items.length > 0 ? items : undefined,
          deadline_duration_seconds,
        });
      } else if (editOrderType) {
        updateOrderTypeMutation.mutate({
          orderTypeId: editOrderType.id,
          name: name.trim(),
          description: description?.trim() || undefined,
          items: items.length > 0 ? items : undefined,
          deadline_duration_seconds,
        });
      }
    } else {
      setEditErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title={isCreate ? "Create Order Template" : "Edit Order Template"}
      isOpen={editOrderType !== null || isCreate}
      onClose={() => {
        if (
          !createOrderTypeMutation.isPending &&
          !updateOrderTypeMutation.isPending
        ) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="order-type-name"
            labelInfo="(required)"
            intent={editErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="order-type-name"
              intent={editErrors.includes("name") ? "danger" : "none"}
              disabled={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
              }
              defaultValue={editOrderType?.name || ""}
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Description" labelFor="order-type-description">
            <TextArea
              id="order-type-description"
              disabled={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
              }
              defaultValue={editOrderType?.description || ""}
              fill
              rows={12}
            />
          </FormGroup>
          <FormGroup
            label="Deadline (hours)"
            labelFor="order-type-deadline-hours"
            labelInfo="(required)"
            intent={editErrors.includes("deadline") ? "danger" : "none"}
            helperText="Order deadline duration as whole hours from creation."
          >
            <NumericInput
              id="order-type-deadline-hours"
              min={0}
              stepSize={1}
              minorStepSize={null}
              value={deadlineHours}
              onValueChange={(v) =>
                setDeadlineHours(typeof v === "number" ? v : Number(v) || 0)
              }
              intent={editErrors.includes("deadline") ? "danger" : "none"}
              disabled={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
              }
            />
          </FormGroup>
          <OrderTypeItemsEditor
            inputId="order-type-items"
            value={items}
            onChange={setItems}
            disabled={
              createOrderTypeMutation.isPending ||
              updateOrderTypeMutation.isPending
            }
          />
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {editErrors.length > 0 &&
              `Missing or invalid fields: ${editErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
              }
              intent="none"
              onClick={() => {
                if (
                  !createOrderTypeMutation.isPending &&
                  !updateOrderTypeMutation.isPending
                ) {
                  onClose();
                }
              }}
            />
            <Button
              text={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
                  ? "Saving..."
                  : isCreate
                    ? "Create"
                    : "Save"
              }
              disabled={
                createOrderTypeMutation.isPending ||
                updateOrderTypeMutation.isPending
              }
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function isOrderTypeDeactivated(orderType: IOrderType): boolean {
  return orderType.deleted_at !== null && orderType.deleted_at !== undefined;
}

export default function AdminInternalOrderTypes() {
  const gate = useInternalCloudAdminGate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuthState();

  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const [editDialogOrderType, setEditDialogOrderType] =
    React.useState<IOrderType | null>(null);

  const [deleteDialogOrderType, setDeleteDialogOrderType] =
    React.useState<IOrderType | null>(null);

  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  const [filterText, setFilterText] = React.useState("");
  const [showDeactivated, setShowDeactivated] = React.useState(false);

  const isFybrAdmin = isFybrAdminUser(currentUser);
  const organizationId = currentUser?.organization_id;

  const { data, isLoading, error } = useQuery({
    queryKey: ["orderTypes", isFybrAdmin, organizationId],
    queryFn: () => {
      if (isFybrAdmin) {
        return ApiAdmin.getAllOrderTypes(true); // Always include deactivated
      } else if (organizationId) {
        return ApiAdmin.getOrderTypesForOrganization(organizationId, true);
      }
      return Promise.resolve({ order_types: [] });
    },
    enabled:
      gate === "allowed" &&
      isFybrAdmin !== undefined &&
      (isFybrAdmin || !!organizationId),
  });

  const deleteOrderTypeMutation = useMutation({
    mutationFn: (orderTypeId: number) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can deactivate order templates")
        );
      }
      return ApiAdmin.deactivateOrderType(orderTypeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderTypes"] });
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: ["orderTypes", organizationId],
        });
      }
      if (toaster.current) {
        toaster.current.show({
          message: "Order template deactivated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      setDeleteDialogOrderType(null);
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to deactivate order template. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const activateOrderTypeMutation = useMutation({
    mutationFn: (orderTypeId: number) => {
      if (!isFybrAdmin) {
        return Promise.reject(
          new Error("Only Fybr admins can activate order templates")
        );
      }
      return ApiAdmin.activateOrderType(orderTypeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderTypes"] });
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: ["orderTypes", organizationId],
        });
      }
      if (toaster.current) {
        toaster.current.show({
          message: "Order template activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onError: (error) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            error.message ||
            "Failed to activate order template. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
  });

  const handleEditOrderType = (orderTypeId: number) => {
    const orderType = data?.order_types.find((ot) => ot.id === orderTypeId);
    if (orderType) {
      setEditDialogOrderType(orderType);
    }
  };

  const handleDeleteOrderType = (orderTypeId: number) => {
    const orderType = data?.order_types.find((ot) => ot.id === orderTypeId);
    if (orderType) {
      setDeleteDialogOrderType(orderType);
    }
  };

  const handleActivateOrderType = (orderTypeId: number) => {
    activateOrderTypeMutation.mutate(orderTypeId);
  };

  const filteredOrderTypes = React.useMemo(() => {
    let filtered =
      data?.order_types.filter((orderType) => {
        if (!showDeactivated && isOrderTypeDeactivated(orderType)) {
          return false;
        }
        if (!filterText.trim()) return true;
        const searchText = filterText.toLowerCase();
        return (
          orderType.name.toLowerCase().includes(searchText) ||
          (orderType.description &&
            orderType.description.toLowerCase().includes(searchText))
        );
      }) || [];

    return filtered.sort((a, b) => {
      const aActive = !isOrderTypeDeactivated(a);
      const bActive = !isOrderTypeDeactivated(b);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [data?.order_types, filterText, showDeactivated]);

  if (gate !== "allowed") return null;

  let content;
  if (isLoading) {
    content = (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  } else if (error) {
    content = (
      <NonIdealState
        icon="error"
        title="Error Loading Order Templates"
        description={error.message}
      />
    );
  } else if (
    filteredOrderTypes.length === 0 &&
    data?.order_types.length > 0 &&
    !showDeactivated
  ) {
    const deactivatedCount = data.order_types.filter(
      isOrderTypeDeactivated
    ).length;
    content = (
      <NonIdealState
        icon="cube"
        title="No Active Order Templates"
        description={`All ${deactivatedCount} order template${
          deactivatedCount !== 1 ? "s" : ""
        } are deactivated. Toggle 'Show deactivated' to view them.`}
        action={
          <Button
            icon="eye-open"
            text="Show Deactivated"
            intent="primary"
            onClick={() => setShowDeactivated(true)}
          />
        }
      />
    );
  } else if (data?.order_types.length === 0) {
    content = (
      <NonIdealState
        icon="cube"
        title="No Order Templates Found"
        description="Get started by adding your first order template."
        action={
          isFybrAdmin ? (
            <Button
              icon="plus"
              text="Add Order Template"
              intent="primary"
              onClick={() => setAddDialogOpen(true)}
            />
          ) : undefined
        }
      />
    );
  } else if (filteredOrderTypes.length === 0 && filterText.trim()) {
    content = (
      <NonIdealState
        icon="search"
        title="No Order Templates Match Filter"
        description={`No order templates found matching "${filterText}".`}
      />
    );
  } else {
    content = (
      <HTMLTable
        striped
        interactive
        css={[
          stickyTableHeaderCss,
          css`
            width: 100%;
          `,
        ]}
      >
        <colgroup>
          <col width="22%" />
          <col />
          <col width="96px" />
          <col width="128px" />
          <col width="64px" />
        </colgroup>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Deadline</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredOrderTypes.map((orderType) => (
            <tr key={orderType.id}>
              <td>
                <div
                  css={css`
                    height: 30px;
                    display: flex;
                    align-items: center;
                    padding-left: 5px;
                    color: ${isOrderTypeDeactivated(orderType)
                      ? "var(--neutral-3)"
                      : "inherit"};
                  `}
                >
                  {orderType.name}
                </div>
              </td>
              <td>
                <div
                  css={css`
                    height: 30px;
                    display: flex;
                    align-items: center;
                    color: ${isOrderTypeDeactivated(orderType)
                      ? "var(--neutral-3)"
                      : "inherit"};
                  `}
                >
                  {orderType.description ? (
                    orderType.description.substring(0, 100) +
                    (orderType.description.length > 100 ? "..." : "")
                  ) : (
                    <span
                      css={css`
                        color: var(--neutral-3);
                        font-style: italic;
                      `}
                    >
                      No description
                    </span>
                  )}
                </div>
              </td>
              <td>
                <div
                  css={css`
                    height: 30px;
                    display: flex;
                    align-items: center;
                    color: ${isOrderTypeDeactivated(orderType)
                      ? "var(--neutral-3)"
                      : "inherit"};
                  `}
                >
                  {orderType.deadline_duration_seconds != null
                    ? `${roundedHoursFromSeconds(orderType.deadline_duration_seconds)} h`
                    : "—"}
                </div>
              </td>
              <td>
                <div
                  css={css`
                    height: 30px;
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Tag
                    intent={
                      isOrderTypeDeactivated(orderType) ? "warning" : "success"
                    }
                    minimal
                  >
                    {isOrderTypeDeactivated(orderType)
                      ? "Deactivated"
                      : "Active"}
                  </Tag>
                </div>
              </td>
              <td>
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 30px;
                    width: 100%;
                  `}
                >
                  <Popover
                    content={
                      <Menu>
                        {isFybrAdmin && !isOrderTypeDeactivated(orderType) && (
                          <MenuItem
                            icon="edit"
                            text="Edit"
                            onClick={() => handleEditOrderType(orderType.id)}
                          />
                        )}
                        {isFybrAdmin &&
                          (isOrderTypeDeactivated(orderType) ? (
                            <MenuItem
                              icon="tick"
                              text="Activate"
                              onClick={() =>
                                handleActivateOrderType(orderType.id)
                              }
                              intent="success"
                            />
                          ) : (
                            <MenuItem
                              icon="disable"
                              text="Deactivate"
                              onClick={() =>
                                handleDeleteOrderType(orderType.id)
                              }
                              intent="danger"
                            />
                          ))}
                      </Menu>
                    }
                    placement="bottom-end"
                  >
                    <Button size="small" icon="more" />
                  </Popover>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </HTMLTable>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
      `}
    >
      {!isLoading && !error && (
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
              flex: 1;
              padding-right: 1rem;
            `}
          >
            <InputGroup
              leftIcon="search"
              placeholder="Filter order templates…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              css={css`
                width: 300px;
              `}
            />
            <Switch
              css={css`
                margin: 0;
              `}
              label="Show deactivated"
              checked={showDeactivated}
              onChange={(e) => setShowDeactivated(e.currentTarget.checked)}
            />
          </div>
          {isFybrAdmin && (
            <Button
              icon="plus"
              text="Add Order Template"
              intent="primary"
              onClick={() => setAddDialogOpen(true)}
            />
          )}
        </div>
      )}
      <div
        css={css`
          flex: 1;
          min-height: 0;
          overflow: auto;
        `}
      >
        {content}
      </div>
      <OrderTypeEditDialog
        key={
          editDialogOrderType
            ? `edit-${editDialogOrderType.id}`
            : addDialogOpen
              ? "create"
              : "closed"
        }
        editOrderType={editDialogOrderType}
        isCreate={addDialogOpen}
        onClose={() => {
          setEditDialogOrderType(null);
          setAddDialogOpen(false);
        }}
      />
      <ConfirmationDialog
        key={
          deleteDialogOrderType
            ? "delete-order-type-dialog"
            : "delete-order-type-dialog-closed"
        }
        entityName={deleteDialogOrderType?.name || ""}
        entityType="Order Template"
        message="Deactivated order templates can't be assigned to organizations or folders."
        isOpen={deleteDialogOrderType !== null}
        onClose={() => setDeleteDialogOrderType(null)}
        onConfirm={() =>
          deleteDialogOrderType &&
          deleteOrderTypeMutation.mutate(deleteDialogOrderType.id)
        }
        isPending={deleteOrderTypeMutation.isPending}
        titleAction="Deactivate"
        actionText="Deactivate"
        actioningText="Deactivating..."
        icon="disable"
      />
    </div>
  );
}
